import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

const Portfolio = () => {
  const { projectId } = useParams();
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeProject, setActiveProject] = useState(null);
  const [filteredProjects, setFilteredProjects] = useState([]);
  
  // Move the projects data definition into useMemo to prevent recreating it on each render
  // while still allowing ESLint to be happy with dependencies
  const projects = useMemo(() => [
    {
      id: 1,
      title: "E-commerce Platform for Irish Retailer",
      slug: "irish-retailer-ecommerce",
      category: "web",
      image: "/images/portfolio/ecommerce.jpg",
      description: "A full-featured e-commerce solution with payment integration for a major Dublin-based retailer.",
      client: "Dublin Retail Co.",
      completionDate: "January 2023",
      services: ["Web Development", "UI/UX Design", "E-Commerce"],
      technologies: ["React", "Node.js", "MongoDB", "Stripe API"],
      challenge: "The client needed a modern, user-friendly e-commerce platform to replace their outdated website. They required a solution that could handle a large product catalog, integrate with their existing inventory system, and provide a seamless shopping experience across all devices.",
      solution: "We developed a custom e-commerce platform using React for the frontend and Node.js for the backend. The solution included advanced search and filtering functionality, product recommendations, secure payment processing through Stripe, and full integration with their inventory management system.",
      results: ["250% increase in online sales within 6 months", "45% reduction in cart abandonment rate", "30% increase in average order value", "Seamless management of over 5,000 products"],
      testimonial: {
        content: "OPITech delivered an exceptional e-commerce platform that far exceeded our expectations. The new site has significantly boosted our online sales and streamlined our operations. Their team was professional, responsive, and truly understood our business needs.",
        author: "Sarah O'Sullivan",
        position: "CEO, Dublin Retail Co."
      },
      imageGallery: [
        "/images/portfolio/ecommerce-1.jpg",
        "/images/portfolio/ecommerce-2.jpg",
        "/images/portfolio/ecommerce-3.jpg"
      ]
    },
    {
      id: 2,
      title: "Fitness Tracking App",
      slug: "fitness-tracking-app",
      category: "mobile",
      image: "/images/portfolio/fitness-app.jpg",
      description: "Mobile application for tracking workouts and nutrition, developed specifically for the Irish fitness market.",
      client: "HealthTrack Ireland",
      completionDate: "March 2023",
      services: ["Mobile App Development", "UI/UX Design"],
      technologies: ["React Native", "Firebase", "Node.js"],
      challenge: "HealthTrack Ireland wanted to create a fitness app tailored specifically for the Irish market. They needed features like workout tracking, nutrition planning, and integration with local fitness centers, all designed with Irish users in mind.",
      solution: "We developed a cross-platform mobile app using React Native, with a clean, intuitive interface for tracking workouts and nutrition. The app includes features like custom workout plans, progress tracking, and social sharing. We integrated with Irish food databases for accurate nutrition information and included location-based features to find nearby fitness centers.",
      results: ["Over 50,000 downloads in the first three months", "Average rating of 4.8/5 stars on app stores", "Featured in 'Top Health & Fitness Apps' in Ireland", "30% higher user retention compared to industry average"],
      testimonial: {
        content: "Working with OPITech was a fantastic experience. They understood our vision and created an app that perfectly meets the needs of our Irish users. Their attention to detail and technical expertise is impressive, and they delivered on time and within budget.",
        author: "James Murphy",
        position: "Founder, HealthTrack Ireland"
      },
      imageGallery: [
        "/images/portfolio/fitness-app-1.jpg",
        "/images/portfolio/fitness-app-2.jpg",
        "/images/portfolio/fitness-app-3.jpg"
      ]
    },
    {
      id: 3,
      title: "Corporate Rebrand & Website",
      slug: "corporate-rebrand-website",
      category: "design",
      image: "/images/portfolio/corporate-site.jpg",
      description: "Complete visual identity redesign and responsive website for a leading Dublin-based technology firm.",
      client: "GreenEnergy Ltd.",
      completionDate: "May 2023",
      services: ["UI/UX Design", "Web Development", "Brand Strategy"],
      technologies: ["Figma", "React", "GSAP", "Tailwind CSS"],
      challenge: "GreenEnergy Ltd. was undergoing a major shift in their business focus and needed a complete rebranding to reflect their new direction. They required a new visual identity and a modern website that would position them as leaders in the renewable energy technology sector.",
      solution: "Our team created a comprehensive brand identity including logo, color palette, typography, and visual elements. We then designed and developed a cutting-edge website featuring dynamic animations, interactive elements, and an intuitive content management system that allows them to easily update their content.",
      results: ["120% increase in website traffic", "35% longer average time on site", "45% increase in quality leads through the website", "Positive reception from industry peers and customers"],
      testimonial: {
        content: "OPITech completely transformed our brand and online presence. Their design work perfectly captures our vision and values, and the website has significantly improved our digital marketing effectiveness. Their collaborative approach made the process smooth and enjoyable.",
        author: "Emma Collins",
        position: "Marketing Director, GreenEnergy Ltd."
      },
      imageGallery: [
        "/images/portfolio/corporate-site-1.jpg",
        "/images/portfolio/corporate-site-2.jpg",
        "/images/portfolio/corporate-site-3.jpg"
      ]
    },
    {
      id: 4,
      title: "Property Management Platform",
      slug: "property-management-platform",
      category: "web",
      image: "/images/portfolio/property-platform.jpg",
      description: "Custom web application for managing residential and commercial properties across Ireland.",
      client: "HomeBase Properties",
      completionDate: "August 2023",
      services: ["Web Development", "Custom Software", "UI/UX Design"],
      technologies: ["Angular", "Node.js", "PostgreSQL", "AWS"],
      challenge: "HomeBase Properties needed a comprehensive platform to manage their growing portfolio of properties. They required features for maintenance requests, tenant communication, rent collection, and detailed analytics across hundreds of properties.",
      solution: "We built a powerful web application with separate portals for property managers, landlords, and tenants. The system includes automated rent collection, maintenance request tracking, document management, and real-time analytics dashboards. The platform is hosted on AWS for maximum reliability and scalability.",
      results: ["70% reduction in administrative workload", "90% faster response time to maintenance requests", "99.9% on-time rent collection rate", "Ability to manage 3x more properties with the same staff"],
      testimonial: {
        content: "This platform has revolutionized our property management operations. What used to take hours now takes minutes, and our tenants love the easy-to-use interface. OPITech took the time to understand our complex needs and delivered a solution that has transformed our business.",
        author: "Michael Kennedy",
        position: "Operations Director, HomeBase Properties"
      },
      imageGallery: [
        "/images/portfolio/property-platform-1.jpg",
        "/images/portfolio/property-platform-2.jpg",
        "/images/portfolio/property-platform-3.jpg"
      ]
    },
    {
      id: 5,
      title: "Restaurant Ordering System",
      slug: "restaurant-ordering-system",
      category: "software",
      image: "/images/portfolio/restaurant-system.jpg",
      description: "Integrated ordering system for a chain of restaurants, with staff and customer interfaces.",
      client: "Taste of Dublin",
      completionDate: "October 2023",
      services: ["Custom Software", "Web Development", "Mobile App Development"],
      technologies: ["React", "React Native", "Node.js", "MongoDB", "Socket.io"],
      challenge: "Taste of Dublin, a growing restaurant chain, needed a modern ordering system to replace their outdated POS. They required a solution that would streamline orders from various sources (in-restaurant, takeaway, online), integrate with kitchen displays, and provide valuable business insights.",
      solution: "We developed an integrated system with three components: a tablet-based POS for staff, a kitchen display system, and a customer-facing mobile app. All components communicate in real-time using Socket.io, ensuring seamless order flow from customer to kitchen and back. The system includes inventory management, sales analytics, and integration with their accounting software.",
      results: ["30% increase in order processing speed", "25% reduction in order errors", "20% increase in average order value through app-based upsells", "Comprehensive business insights leading to menu optimization"],
      testimonial: {
        content: "The ordering system developed by OPITech has been a game-changer for our restaurants. It's efficient, reliable, and has significantly improved both our operations and customer experience. Their team showed remarkable understanding of the hospitality industry.",
        author: "Laura Ryan",
        position: "Operations Manager, Taste of Dublin"
      },
      imageGallery: [
        "/images/portfolio/restaurant-system-1.jpg",
        "/images/portfolio/restaurant-system-2.jpg",
        "/images/portfolio/restaurant-system-3.jpg"
      ]
    },
    {
      id: 6,
      title: "Travel Companion App",
      slug: "travel-companion-app",
      category: "mobile",
      image: "/images/portfolio/travel-app.jpg",
      description: "Mobile application showcasing Irish tourist destinations with AR features and personalized itineraries.",
      client: "IrishJourneys",
      completionDate: "December 2023",
      services: ["Mobile App Development", "UI/UX Design", "AR Development"],
      technologies: ["React Native", "ARKit", "ARCore", "Firebase", "Google Maps API"],
      challenge: "IrishJourneys wanted to create an innovative mobile app that would showcase Ireland's tourist destinations in a unique way. They needed features like augmented reality for historical sites, personalized trip planning, and offline functionality for areas with poor connectivity.",
      solution: "We created a feature-rich travel companion app with stunning UI and seamless UX. The app includes AR views of historical sites showing how they looked in the past, personalized trip recommendations based on user preferences, interactive maps, and comprehensive offline functionality. We also included user-generated content features to build community engagement.",
      results: ["Featured as 'App of the Day' in the App Store", "Over 75,000 downloads in the first two months", "Average session duration of 15 minutes", "High user engagement with 60% of users creating personalized itineraries"],
      testimonial: {
        content: "OPITech delivered an exceptional app that has transformed how people explore Ireland. The AR features have received particular praise, and the intuitive design makes it accessible for travelers of all ages. Their technical innovation and creativity exceeded our expectations.",
        author: "Patrick O'Connor",
        position: "Director, IrishJourneys"
      },
      imageGallery: [
        "/images/portfolio/travel-app-1.jpg",
        "/images/portfolio/travel-app-2.jpg",
        "/images/portfolio/travel-app-3.jpg"
      ]
    }
  ], []);
  
  // Categories for filter buttons
  const categories = [
    { id: 'all', label: 'All Projects' },
    { id: 'web', label: 'Web' },
    { id: 'mobile', label: 'Mobile' },
    { id: 'design', label: 'Design' },
    { id: 'software', label: 'Software' }
  ];
  
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // If projectId is provided, find and set the active project
    if (projectId) {
      const numId = parseInt(projectId, 10);
      const project = projects.find(p => p.id === numId || p.slug === projectId);
      if (project) {
        setActiveProject(project);
      } else {
        setActiveProject(null);
      }
    } else {
      setActiveProject(null);
    }
    
    // Filter projects based on activeFilter
    if (activeFilter === 'all') {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter(project => project.category === activeFilter);
      setFilteredProjects(filtered);
    }
  }, [projectId, activeFilter, projects]); // Added projects to the dependency array
  
  // If a specific project is selected, show its detailed view
  if (activeProject) {
    return (
      <>
        {/* Hero Section */}
        <section className="hero" style={{ backgroundColor: 'var(--dark-light)' }}>
          <div className="container">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <Link to="/portfolio" className="btn btn-outline btn-sm">
                <i className="fas fa-arrow-left"></i> All Projects
              </Link>
            </div>
            <h1>{activeProject.title}</h1>
            <p className="hero-description">
              {activeProject.description}
            </p>
          </div>
        </section>
        
        {/* Project Overview */}
        <section className="section">
          <div className="container">
            <div className="grid grid-2">
              <div>
                <img 
                  src={activeProject.image} 
                  alt={activeProject.title} 
                  style={{ 
                    width: '100%', 
                    borderRadius: 'var(--radius)', 
                    marginBottom: '2rem',
                    boxShadow: 'var(--shadow)'
                  }} 
                />
                
                <div style={{ marginBottom: '2rem' }}>
                  <h3 className="mb-3">Project Gallery</h3>
                  <div className="grid grid-3" style={{ gap: '1rem' }}>
                    {activeProject.imageGallery.map((image, index) => (
                      <img 
                        key={index} 
                        src={image} 
                        alt={`${activeProject.title} - Gallery ${index + 1}`} 
                        style={{ width: '100%', borderRadius: 'var(--radius)', height: '150px', objectFit: 'cover' }} 
                      />
                    ))}
                  </div>
                </div>
              </div>
              
              <div>
                <h2 className="mb-4">Project Details</h2>
                
                <div className="card" style={{ padding: '1.5rem', marginBottom: '2rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <h4 className="mb-1">Client</h4>
                      <p className="mb-3">{activeProject.client}</p>
                    </div>
                    <div>
                      <h4 className="mb-1">Completed</h4>
                      <p className="mb-3">{activeProject.completionDate}</p>
                    </div>
                    <div>
                      <h4 className="mb-1">Services</h4>
                      <p className="mb-3">{activeProject.services.join(', ')}</p>
                    </div>
                    <div>
                      <h4 className="mb-1">Technologies</h4>
                      <p className="mb-3">{activeProject.technologies.join(', ')}</p>
                    </div>
                  </div>
                </div>
                
                <h3 className="mb-3">The Challenge</h3>
                <p className="mb-4">{activeProject.challenge}</p>
                
                <h3 className="mb-3">Our Solution</h3>
                <p className="mb-4">{activeProject.solution}</p>
                
                <h3 className="mb-3">Results</h3>
                <ul style={{ listStyle: 'none', padding: 0, marginBottom: '2rem' }}>
                  {activeProject.results.map((result, index) => (
                    <li key={index} style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                      <i className="fas fa-check-circle" style={{ color: 'var(--secondary)', marginTop: '0.25rem' }}></i>
                      <span>{result}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="card" style={{ padding: '1.5rem', marginBottom: '2rem' }}>
                  <h3 className="mb-3">Client Testimonial</h3>
                  <p className="mb-3" style={{ fontStyle: 'italic' }}>"{activeProject.testimonial.content}"</p>
                  <div>
                    <strong>{activeProject.testimonial.author}</strong>
                    <p>{activeProject.testimonial.position}</p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <Link to="/contact" className="btn">
                    Start Your Project
                  </Link>
                  <Link to="/services" className="btn btn-outline">
                    Our Services
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Other Projects */}
        <section className="section" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2>More Projects</h2>
              <p>Explore more of our work</p>
            </div>
            
            <div className="portfolio-grid">
              {projects
                .filter(project => project.id !== activeProject.id)
                .slice(0, 3)
                .map(project => (
                  <div key={project.id} className="portfolio-item">
                    <img src={project.image} alt={project.title} className="portfolio-image" />
                    <div className="portfolio-overlay">
                      <p className="portfolio-category">{project.category}</p>
                      <h3 className="portfolio-title">{project.title}</h3>
                      <Link to={`/portfolio/${project.id}`} className="portfolio-link">
                        View Details <i className="fas fa-arrow-right ml-1"></i>
                      </Link>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="cta-section">
          <div className="container cta-content">
            <h2 className="cta-title">Have a Similar Project in Mind?</h2>
            <p className="cta-description">
              Let's discuss how we can create a custom solution for your business needs.
            </p>
            <Link to="/contact" className="btn btn-secondary">
              Get a Free Consultation
            </Link>
          </div>
        </section>
      </>
    );
  }
  
  // Main portfolio page
  return (
    <>
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <h1>Our Portfolio</h1>
          <p className="hero-description">
            Explore our recent work and see how we've helped businesses achieve their digital goals.
          </p>
        </div>
      </section>
      
      {/* Portfolio Grid */}
      <section className="section">
        <div className="container">
          <div className="portfolio-filters">
            {categories.map(category => (
              <button 
                key={category.id}
                className={`portfolio-filter ${activeFilter === category.id ? 'active' : ''}`}
                onClick={() => setActiveFilter(category.id)}
              >
                {category.label}
              </button>
            ))}
          </div>
          
          <div className="portfolio-grid">
            {filteredProjects.map(project => (
              <div key={project.id} className="portfolio-item">
                <img src={project.image} alt={project.title} className="portfolio-image" />
                <div className="portfolio-overlay">
                  <p className="portfolio-category">{project.category}</p>
                  <h3 className="portfolio-title">{project.title}</h3>
                  <p>Client: {project.client}</p>
                  <Link to={`/portfolio/${project.id}`} className="portfolio-link">
                    View Details <i className="fas fa-arrow-right ml-1"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
          
          {filteredProjects.length === 0 && (
            <div className="text-center" style={{ padding: '3rem' }}>
              <h3>No projects found</h3>
              <p>No projects match this filter. Please try another category.</p>
            </div>
          )}
        </div>
      </section>
      
      {/* Client Logo Section */}
      <section className="section" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2>Our Clients</h2>
            <p>Trusted by businesses across Ireland and beyond</p>
          </div>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '3rem', alignItems: 'center' }}>
            {/* In a real project, these would be actual client logos */}
            <div style={{ width: '150px', height: '80px', background: 'rgba(255, 255, 255, 0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius)' }}>
              <span style={{ color: 'var(--gray)' }}>Client Logo</span>
            </div>
            <div style={{ width: '150px', height: '80px', background: 'rgba(255, 255, 255, 0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius)' }}>
              <span style={{ color: 'var(--gray)' }}>Client Logo</span>
            </div>
            <div style={{ width: '150px', height: '80px', background: 'rgba(255, 255, 255, 0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius)' }}>
              <span style={{ color: 'var(--gray)' }}>Client Logo</span>
            </div>
            <div style={{ width: '150px', height: '80px', background: 'rgba(255, 255, 255, 0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius)' }}>
              <span style={{ color: 'var(--gray)' }}>Client Logo</span>
            </div>
            <div style={{ width: '150px', height: '80px', background: 'rgba(255, 255, 255, 0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius)' }}>
              <span style={{ color: 'var(--gray)' }}>Client Logo</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* Statistics Section */}
      <section className="section">
        <div className="container">
          <div className="grid grid-4">
            <div className="text-center">
              <h2 style={{ 
                fontSize: '3rem', 
                background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                '-webkit-background-clip': 'text',
                '-webkit-text-fill-color': 'transparent',
                'backgroundClip': 'text'
              }}>
                50+
              </h2>
              <p>Completed Projects</p>
            </div>
            
            <div className="text-center">
              <h2 style={{ 
                fontSize: '3rem', 
                background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                '-webkit-background-clip': 'text',
                '-webkit-text-fill-color': 'transparent',
                'backgroundClip': 'text'
              }}>
                30+
              </h2>
              <p>Happy Clients</p>
            </div>
            
            <div className="text-center">
              <h2 style={{ 
                fontSize: '3rem', 
                background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                '-webkit-background-clip': 'text',
                '-webkit-text-fill-color': 'transparent',
                'backgroundClip': 'text'
              }}>
                5+
              </h2>
              <p>Years Experience</p>
            </div>
            
            <div className="text-center">
              <h2 style={{ 
                fontSize: '3rem', 
                background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                '-webkit-background-clip': 'text',
                '-webkit-text-fill-color': 'transparent',
                'backgroundClip': 'text'
              }}>
                10+
              </h2>
              <p>Industry Awards</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="cta-section">
        <div className="container cta-content">
          <h2 className="cta-title">Ready to Start Your Project?</h2>
          <p className="cta-description">
            Let us help you bring your vision to life with our expertise in web and mobile development.
          </p>
          <Link to="/contact" className="btn btn-secondary">
            Get a Free Consultation
          </Link>
        </div>
      </section>
    </>
  );
};

export default Portfolio;